/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import {
  Button,
  Form,
  Icon,
  Label,
  Message,
  Modal,
  Pagination,
  Select,
  Table,
} from 'semantic-ui-react';
import {
  API,
  copy,
  isAdmin,
  showError,
  showSuccess,
  showWarning,
  timestamp2string,
} from '../helpers';
import { ITEMS_PER_PAGE } from '../constants';
import { renderColorLabel } from '../helpers/render';
import RequestLogPayloadNotice from './RequestLogPayloadNotice';
import { isRawStream, isSummaryLog } from '../helpers/logPayload';

const REQUEST_TYPE_OPTIONS = [
  { key: 'all', text: '全部', value: '' },
  { key: 'text', text: '文本', value: 'text' },
  { key: 'image', text: '图片', value: 'image' },
  { key: 'video', text: '视频', value: 'video' },
  { key: 'unknown', text: '未知类型', value: 'unknown' },
];

function buildInitialInputs(now) {
  const params = new URLSearchParams(window.location.search);
  const timestampValue = (key, fallback) => {
    const value = Number(params.get(key));
    return timestamp2string(
      Number.isFinite(value) && value > 0 ? value : fallback
    );
  };
  return {
    request_type: params.get('request_type') || '',
    model_name: params.get('model_name') || '',
    token_name: '',
    request_id: '',
    task_id: '',
    username: '',
    channel_id: params.get('channel_id') || '',
    status_code: params.get('status_code') || '',
    failure_code: params.get('failure_code') || '',
    start_timestamp: timestampValue('start_timestamp', 0),
    end_timestamp: timestampValue('end_timestamp', now.getTime() / 1000 + 3600),
  };
}

function renderRequestType(type) {
  switch (type) {
    case 'text':
      return (
        <Label basic color='blue'>
          文本
        </Label>
      );
    case 'image':
      return (
        <Label basic color='purple'>
          图片
        </Label>
      );
    case 'video':
      return (
        <Label basic color='teal'>
          视频
        </Label>
      );
    default:
      return (
        <Label basic color='grey'>
          {type || '未知'}
        </Label>
      );
  }
}

function renderStatusCode(code) {
  if (!code) return '';
  let color = 'grey';
  if (code >= 500) color = 'red';
  else if (code >= 400) color = 'yellow';
  else if (code >= 200 && code < 300) color = 'green';
  return (
    <Label basic color={color}>
      {code}
    </Label>
  );
}

function renderElapsedTime(ms) {
  if (!ms) return '';
  let color = 'green';
  if (ms >= 10000) color = 'red';
  else if (ms >= 5000) color = 'orange';
  else if (ms >= 3000) color = 'yellow';
  else if (ms >= 1000) color = 'olive';
  return (
    <Label basic size='mini' color={color}>
      {ms} ms
    </Label>
  );
}

const ErrorLogsTable = () => {
  const isAdminUser = isAdmin();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activePage, setActivePage] = useState(1);
  const [selectedLog, setSelectedLog] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [requestLogData, setRequestLogData] = useState({
    data: null,
    loading: false,
    loaded: false,
  });

  const now = new Date();
  const [inputs, setInputs] = useState(() => buildInitialInputs(now));
  const initialParams = new URLSearchParams(window.location.search);
  const fromStats = initialParams.get('from') === 'stats';
  initialParams.delete('from');
  const statsReturnUrl = `/error_log/stats?${initialParams.toString()}`;

  const {
    request_type,
    model_name,
    token_name,
    request_id,
    task_id,
    username,
    channel_id,
    status_code,
    failure_code,
    start_timestamp,
    end_timestamp,
  } = inputs;

  const handleInputChange = (e, { name, value }) => {
    setInputs((prev) => ({ ...prev, [name]: value }));
  };

  const loadLogs = async (startIdx) => {
    const localStartTimestamp = Date.parse(start_timestamp) / 1000;
    const localEndTimestamp = Date.parse(end_timestamp) / 1000;

    const params = new URLSearchParams({
      p: startIdx,
      request_type,
      model_name,
      token_name,
      request_id,
      task_id,
      start_timestamp: localStartTimestamp,
      end_timestamp: localEndTimestamp,
    });

    if (isAdminUser) {
      if (username) params.append('username', username);
      if (channel_id) params.append('channel_id', channel_id);
      if (status_code) params.append('status_code', status_code);
      if (failure_code) params.append('failure_code', failure_code);
    }

    const url = isAdminUser
      ? `/api/error_log/?${params.toString()}`
      : `/api/error_log/self?${params.toString()}`;

    const res = await API.get(url);
    const { success, message, data } = res.data;
    if (success) {
      if (startIdx === 0) {
        setLogs(data);
      } else {
        const newLogs = [...logs];
        newLogs.splice(startIdx * ITEMS_PER_PAGE, data.length, ...data);
        setLogs(newLogs);
      }
    } else {
      showError(message);
    }
    setLoading(false);
  };

  const refresh = async () => {
    setLoading(true);
    setActivePage(1);
    await loadLogs(0);
  };

  const onPaginationChange = (e, { activePage }) => {
    (async () => {
      if (activePage === Math.ceil(logs.length / ITEMS_PER_PAGE) + 1) {
        await loadLogs(activePage - 1);
      }
      setActivePage(activePage);
    })();
  };

  useEffect(() => {
    refresh().then();
  }, []);

  const formatJSON = (str) => {
    if (!str) return '';
    try {
      return JSON.stringify(JSON.parse(str), null, 2);
    } catch {
      return str;
    }
  };

  const copyText = async (text, label) => {
    if (await copy(text)) {
      showSuccess(`已复制${label}`);
    }
  };

  const openDetail = (log) => {
    setSelectedLog(log);
    setRequestLogData({ data: null, loading: false, loaded: false });
    setModalOpen(true);
  };

  const hasRequestLog = (log) => Boolean(log?.request_id);

  const loadRequestLog = async (requestId, createdAt) => {
    if (!requestId) {
      showWarning('该记录没有 request_id');
      return;
    }
    setRequestLogData({ data: null, loading: true, loaded: false });
    try {
      const res = await API.get(`/api/request_log/${requestId}`, {
        params: createdAt ? { created_at: createdAt } : {},
      });
      const { success, data, message } = res.data;
      if (success && data) {
        setRequestLogData({ data, loading: false, loaded: true });
      } else {
        setRequestLogData({ data: null, loading: false, loaded: true });
        if (message) {
          showError(message);
        }
      }
    } catch {
      setRequestLogData({ data: null, loading: false, loaded: true });
    }
  };

  return (
    <>
      {fromStats && (
        <Message info size='small'>
          <Icon name='filter' />
          已按统计项带入筛选条件。
          <Button
            as='a'
            href={statsReturnUrl}
            size='mini'
            basic
            primary
            floated='right'
          >
            返回错误日志统计
          </Button>
        </Message>
      )}
      <Form>
        <Form.Group>
          <Form.Field width={3}>
            <label>请求类型</label>
            <Select
              options={REQUEST_TYPE_OPTIONS}
              value={request_type}
              name='request_type'
              onChange={handleInputChange}
              placeholder='全部'
            />
          </Form.Field>
          <Form.Input
            fluid
            label='模型名称'
            size='small'
            width={3}
            value={model_name}
            placeholder='输入模型名称'
            name='model_name'
            onChange={handleInputChange}
          />
          <Form.Input
            fluid
            label='Token 名称'
            size='small'
            width={3}
            value={token_name}
            placeholder='输入 Token 名称'
            name='token_name'
            onChange={handleInputChange}
          />
          <Form.Input
            fluid
            label='开始时间'
            size='small'
            width={4}
            value={start_timestamp}
            type='datetime-local'
            name='start_timestamp'
            onChange={handleInputChange}
          />
          <Form.Input
            fluid
            label='结束时间'
            size='small'
            width={4}
            value={end_timestamp}
            type='datetime-local'
            name='end_timestamp'
            onChange={handleInputChange}
          />
          <Form.Button
            fluid
            label='查询'
            size='small'
            width={2}
            onClick={refresh}
          >
            查询
          </Form.Button>
        </Form.Group>
        <Form.Group>
          <Form.Input
            fluid
            label='Request ID'
            size='small'
            width={3}
            value={request_id}
            placeholder='输入 Request ID'
            name='request_id'
            onChange={handleInputChange}
          />
          <Form.Input
            fluid
            label='Task ID'
            size='small'
            width={3}
            value={task_id}
            placeholder='输入 Task ID'
            name='task_id'
            onChange={handleInputChange}
          />
          {isAdminUser && (
            <>
              <Form.Input
                fluid
                label='用户名'
                size='small'
                width={2}
                value={username}
                placeholder='输入用户名'
                name='username'
                onChange={handleInputChange}
              />
              <Form.Input
                fluid
                label='渠道 ID'
                size='small'
                width={2}
                value={channel_id}
                placeholder='渠道 ID'
                name='channel_id'
                onChange={handleInputChange}
              />
              <Form.Input
                fluid
                label='HTTP 状态码'
                size='small'
                width={2}
                value={status_code}
                placeholder='如 500'
                name='status_code'
                onChange={handleInputChange}
              />
              <Form.Input
                fluid
                label='失败码'
                size='small'
                width={4}
                value={failure_code}
                placeholder='失败码（时间范围需≤90天）'
                name='failure_code'
                onChange={handleInputChange}
              />
            </>
          )}
        </Form.Group>
      </Form>

      <div style={{ overflowX: 'auto' }}>
        <Table basic='very' compact size='small' style={{ minWidth: '900px' }}>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell width={2}>时间</Table.HeaderCell>
              <Table.HeaderCell width={1}>类型</Table.HeaderCell>
              <Table.HeaderCell width={2}>模型</Table.HeaderCell>
              {isAdminUser && (
                <Table.HeaderCell width={2}>渠道</Table.HeaderCell>
              )}
              <Table.HeaderCell width={2}>Token</Table.HeaderCell>
              {isAdminUser && (
                <Table.HeaderCell width={1}>用户</Table.HeaderCell>
              )}
              <Table.HeaderCell width={1}>状态码</Table.HeaderCell>
              <Table.HeaderCell width={2}>错误类型</Table.HeaderCell>
              <Table.HeaderCell width={1}>耗时</Table.HeaderCell>
              <Table.HeaderCell width={1}>异步</Table.HeaderCell>
              <Table.HeaderCell width={1}>详情</Table.HeaderCell>
            </Table.Row>
          </Table.Header>

          <Table.Body>
            {logs
              .slice(
                (activePage - 1) * ITEMS_PER_PAGE,
                activePage * ITEMS_PER_PAGE
              )
              .map((log) => (
                <Table.Row key={log.id}>
                  <Table.Cell>
                    <code style={{ fontSize: '0.85em' }}>
                      {timestamp2string(log.created_at)}
                    </code>
                  </Table.Cell>
                  <Table.Cell>{renderRequestType(log.request_type)}</Table.Cell>
                  <Table.Cell>
                    {log.model_name ? renderColorLabel(log.model_name) : ''}
                  </Table.Cell>
                  {isAdminUser && (
                    <Table.Cell>
                      {log.channel_name ? (
                        renderColorLabel(log.channel_name)
                      ) : log.channel_id ? (
                        <Label basic size='small'>
                          {log.channel_id}
                        </Label>
                      ) : (
                        ''
                      )}
                    </Table.Cell>
                  )}
                  <Table.Cell>
                    {log.token_name ? renderColorLabel(log.token_name) : ''}
                  </Table.Cell>
                  {isAdminUser && <Table.Cell>{log.username || ''}</Table.Cell>}
                  <Table.Cell>{renderStatusCode(log.status_code)}</Table.Cell>
                  <Table.Cell>
                    {log.error_type ? (
                      <Label basic size='small' color='orange'>
                        {log.error_type}
                      </Label>
                    ) : (
                      ''
                    )}
                  </Table.Cell>
                  <Table.Cell>{renderElapsedTime(log.elapsed_time)}</Table.Cell>
                  <Table.Cell>
                    {log.is_async ? (
                      <Label basic size='mini' color='teal'>
                        异步
                      </Label>
                    ) : (
                      <Label basic size='mini' color='grey'>
                        同步
                      </Label>
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    <Button
                      size='mini'
                      basic
                      color='blue'
                      onClick={() => openDetail(log)}
                    >
                      详情
                    </Button>
                  </Table.Cell>
                </Table.Row>
              ))}
          </Table.Body>

          <Table.Footer>
            <Table.Row>
              <Table.HeaderCell colSpan={isAdminUser ? '11' : '9'}>
                <Button size='small' onClick={refresh} loading={loading}>
                  刷新
                </Button>
                <Pagination
                  floated='right'
                  activePage={activePage}
                  onPageChange={onPaginationChange}
                  size='small'
                  siblingRange={1}
                  totalPages={
                    Math.ceil(logs.length / ITEMS_PER_PAGE) +
                    (logs.length % ITEMS_PER_PAGE === 0 ? 1 : 0)
                  }
                />
              </Table.HeaderCell>
            </Table.Row>
          </Table.Footer>
        </Table>
      </div>

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} size='small'>
        <Modal.Header>错误详情</Modal.Header>
        <Modal.Content>
          {selectedLog && (
            <div>
              <p>
                <strong>时间：</strong>
                {timestamp2string(selectedLog.created_at)}
              </p>
              <p>
                <strong>请求类型：</strong>
                {selectedLog.request_type}
              </p>
              <p>
                <strong>模型：</strong>
                {selectedLog.model_name}
              </p>
              {isAdminUser && (
                <p>
                  <strong>渠道：</strong>
                  {selectedLog.channel_name || selectedLog.channel_id}
                </p>
              )}
              <p>
                <strong>Token：</strong>
                {selectedLog.token_name}
              </p>
              {isAdminUser && (
                <p>
                  <strong>用户：</strong>
                  {selectedLog.username}
                </p>
              )}
              <p>
                <strong>HTTP 状态码：</strong>
                {selectedLog.status_code}
              </p>
              <p>
                <strong>错误类型：</strong>
                {selectedLog.error_type}
              </p>
              <p>
                <strong>错误代码：</strong>
                {selectedLog.error_code}
              </p>
              <p>
                <strong>Request ID：</strong>
                <code>{selectedLog.request_id}</code>
              </p>
              <p>
                <strong>Task ID：</strong>
                <code>{selectedLog.task_id}</code>
              </p>
              <p>
                <strong>耗时：</strong>
                {selectedLog.elapsed_time} ms
              </p>
              <p>
                <strong>是否异步：</strong>
                {selectedLog.is_async ? '是' : '否'}
              </p>
              <div>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <strong>错误信息：</strong>
                  <Button
                    size='mini'
                    basic
                    icon='copy'
                    content='复制'
                    onClick={() =>
                      copyText(
                        selectedLog.error_message || '（无）',
                        '错误信息'
                      )
                    }
                  />
                </div>
                <pre
                  style={{
                    background: '#f5f5f5',
                    padding: '8px',
                    borderRadius: '4px',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-all',
                    maxHeight: '200px',
                    overflowY: 'auto',
                    fontSize: '0.85em',
                    marginTop: '4px',
                  }}
                >
                  {selectedLog.error_message || '（无）'}
                </pre>
              </div>
              {selectedLog.request_body && (
                <div style={{ marginTop: '8px' }}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <strong>请求体：</strong>
                    <Button
                      size='mini'
                      basic
                      icon='copy'
                      content='复制'
                      onClick={() =>
                        copyText(selectedLog.request_body, '请求体')
                      }
                    />
                  </div>
                  <pre
                    style={{
                      background: '#e8f4f8',
                      padding: '8px',
                      borderRadius: '4px',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-all',
                      maxHeight: '200px',
                      overflowY: 'auto',
                      fontSize: '0.85em',
                      marginTop: '4px',
                    }}
                  >
                    {selectedLog.request_body}
                  </pre>
                </div>
              )}
              {selectedLog.raw_response && (
                <div style={{ marginTop: '8px' }}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <strong>上游原始响应：</strong>
                    <Button
                      size='mini'
                      basic
                      icon='copy'
                      content='复制'
                      onClick={() =>
                        copyText(selectedLog.raw_response, '上游原始响应')
                      }
                    />
                  </div>
                  <pre
                    style={{
                      background: '#fff3cd',
                      padding: '8px',
                      borderRadius: '4px',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-all',
                      maxHeight: '200px',
                      overflowY: 'auto',
                      fontSize: '0.85em',
                      marginTop: '4px',
                    }}
                  >
                    {selectedLog.raw_response}
                  </pre>
                </div>
              )}
              <div
                style={{
                  marginTop: '16px',
                  borderTop: '1px solid #eee',
                  paddingTop: '12px',
                }}
              >
                {hasRequestLog(selectedLog) ? (
                  <>
                    {!requestLogData.loaded && !requestLogData.loading && (
                      <Button
                        data-testid='request-log-button'
                        size='small'
                        basic
                        color='blue'
                        onClick={() =>
                          loadRequestLog(
                            selectedLog.request_id,
                            selectedLog.created_at,
                          )
                        }
                      >
                        查看请求/响应日志
                      </Button>
                    )}
                    {requestLogData.loading && <p>加载中...</p>}
                    {requestLogData.loaded && !requestLogData.data && (
                      <p style={{ color: '#999' }}>
                        无完整请求/响应日志（可能未开启日志记录）
                      </p>
                    )}
                    {requestLogData.data && (
                      <>
                        <RequestLogPayloadNotice log={requestLogData.data} />
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <strong>{isSummaryLog(requestLogData.data) ? '请求摘要：' : '完整请求体：'}</strong>
                          <Button
                            size='mini'
                            basic
                            icon='copy'
                            content={isSummaryLog(requestLogData.data) ? '复制摘要' : '复制'}
                            onClick={() =>
                              copyText(
                                formatJSON(requestLogData.data.request_body),
                                isSummaryLog(requestLogData.data) ? '请求摘要' : '完整请求体'
                              )
                            }
                          />
                        </div>
                        <pre
                          style={{
                            background: '#f5f5f5',
                            padding: '8px',
                            borderRadius: '4px',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-all',
                            maxHeight: '300px',
                            overflowY: 'auto',
                            fontSize: '0.85em',
                            marginTop: '4px',
                          }}
                        >
                          {formatJSON(requestLogData.data.request_body)}
                        </pre>
                        {requestLogData.data.response_headers && (
                          <div style={{ marginTop: '8px' }}>
                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                              }}
                            >
                              <strong>响应头：</strong>
                              <Button
                                size='mini'
                                basic
                                icon='copy'
                                content='复制'
                                onClick={() =>
                                  copyText(
                                    formatJSON(
                                      requestLogData.data.response_headers
                                    ),
                                    '响应头'
                                  )
                                }
                              />
                            </div>
                            <pre
                              style={{
                                background: '#f0f7ff',
                                padding: '8px',
                                borderRadius: '4px',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-all',
                                maxHeight: '200px',
                                overflowY: 'auto',
                                fontSize: '0.85em',
                                marginTop: '4px',
                              }}
                            >
                              {formatJSON(requestLogData.data.response_headers)}
                            </pre>
                          </div>
                        )}
                        <div style={{ marginTop: '8px' }}>
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                            }}
                          >
                            <strong>{isSummaryLog(requestLogData.data) ? '响应摘要：' : '完整响应体：'}</strong>
                            <Button
                              size='mini'
                              basic
                              icon='copy'
                              content={isSummaryLog(requestLogData.data) ? '复制摘要' : '复制'}
                              onClick={() =>
                                copyText(
                                  isRawStream(requestLogData.data)
                                    ? requestLogData.data.response_body
                                    : formatJSON(
                                        requestLogData.data.response_body
                                      ),
                                  isSummaryLog(requestLogData.data) ? '响应摘要' : '完整响应体'
                                )
                              }
                            />
                          </div>
                          <pre
                            style={{
                              background: '#f5f5f5',
                              padding: '8px',
                              borderRadius: '4px',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-all',
                              maxHeight: '300px',
                              overflowY: 'auto',
                              fontSize: '0.85em',
                              marginTop: '4px',
                            }}
                          >
                            {isRawStream(requestLogData.data)
                              ? requestLogData.data.response_body
                              : formatJSON(requestLogData.data.response_body)}
                          </pre>
                        </div>
                      </>
                    )}
                  </>
                ) : (
                  <p
                    data-testid='request-log-unavailable'
                    style={{ color: '#999', margin: 0 }}
                  >
                    该错误日志没有关联的完整请求/响应记录
                  </p>
                )}
              </div>
            </div>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setModalOpen(false)}>关闭</Button>
        </Modal.Actions>
      </Modal>
    </>
  );
};

export default ErrorLogsTable;
